/**
 * Client mirror of backend/notification_policy.py.
 *
 * The server decides how a *push* behaves and marks it (``data.persistent_alert`` / ``alert_repeat`` /
 * ``alert_sound``), so anything arriving as a push needs nothing from here. This exists for the alerts the app
 * raises from its own polling — the merchant Live Orders refresh and the rider home refresh, which are what keeps
 * a console alerting while a push is in flight or absent. Those have to reach the same verdict the server would,
 * or the same order alerts differently depending on which path noticed it first.
 *
 * Keep the event keys, modes and defaults in step with notification_policy.py.
 */

export const MODE_PUSH = "PUSH";
export const MODE_FULL_SCREEN = "FULL_SCREEN";
export const MODE_DISABLED = "DISABLED";
export const MODES = [MODE_PUSH, MODE_FULL_SCREEN, MODE_DISABLED];

export const EVENT_NEW_ORDER = "new_order";
export const EVENT_NEW_RIDE = "new_ride";
export const EVENT_ORDER_CANCELLED = "order_cancelled";
export const OTHER_EVENT = "other";

/** Same catalogue the backend serves; used as the fallback when the config API hasn't answered yet. */
export const EVENTS = [
    { key: EVENT_NEW_ORDER, label: "New order", supports_full_screen: true },
    { key: EVENT_NEW_RIDE, label: "Delivery request", supports_full_screen: true },
    { key: EVENT_ORDER_CANCELLED, label: "Order cancelled", supports_full_screen: true },
    { key: "order_status", label: "Order status updates", supports_full_screen: false },
    { key: "booking", label: "Bookings", supports_full_screen: false },
    { key: "order_return", label: "Returns", supports_full_screen: false },
    { key: "account", label: "Wallet and coupon reminders", supports_full_screen: false },
    { key: OTHER_EVENT, label: "Other notifications", supports_full_screen: false },
];

const TOPIC_TO_EVENT = {
    order_placed: EVENT_NEW_ORDER,
    rider_order_available: EVENT_NEW_RIDE,
    ride_available: EVENT_NEW_RIDE,
    order_cancelled: EVENT_ORDER_CANCELLED,
    order_confirmed: "order_status",
    order_preparing: "order_status",
    order_ready: "order_status",
    order_out_for_delivery: "order_status",
    order_picked_up: "order_status",
    order_delivered: "order_status",
    order_refunded: "order_status",
    booking_confirmed: "booking",
    booking_completed: "booking",
    booking_cancelled: "booking",
    order_return_requested: "order_return",
    order_return_approved: "order_return",
    order_return_rejected: "order_return",
    wallet_admin_adjustment: "account",
    coupon_expiring_soon: "account",
};

/** Events that can ever show the in-app alert (the rest are ordinary pushes, exactly as before). */
export const FULL_SCREEN_EVENTS = new Set([EVENT_NEW_ORDER, EVENT_NEW_RIDE, EVENT_ORDER_CANCELLED]);

export function eventForTopic(topic) {
    return TOPIC_TO_EVENT[topic] || OTHER_EVENT;
}

/** The legacy Super-Admin opt-in: a custom alert sound or loud mode. */
export function legacyAlertConfigured(alertCfg) {
    return !!(alertCfg?.sound_key || alertCfg?.loud_mode_enabled);
}

/**
 * ``{mode, sound, repeat}`` for one event on this tenant — the same answer notification_policy.resolve gives for a
 * staff recipient. With no rule saved it falls back to the legacy behaviour, so a tenant that never opens the new
 * settings page behaves exactly as it does today.
 */
export function resolveRule(tenant, eventKey) {
    const rule = tenant?.notification_rules?.[eventKey];
    const legacyFullScreen = FULL_SCREEN_EVENTS.has(eventKey) && legacyAlertConfigured(tenant?.new_order_alert_config);
    let mode = MODES.includes(rule?.mode) ? rule.mode : (legacyFullScreen ? MODE_FULL_SCREEN : MODE_PUSH);
    if (mode === MODE_FULL_SCREEN && !FULL_SCREEN_EVENTS.has(eventKey)) mode = MODE_PUSH;
    const sound = typeof rule?.sound === "boolean" ? rule.sound : true;
    const repeat = mode === MODE_FULL_SCREEN
        ? (typeof rule?.repeat === "boolean" ? rule.repeat : true)
        : false;
    return { mode, sound, repeat };
}

/** Whether this tenant shows the in-app alert for an event — what the polling refreshes check before raising one. */
export function isFullScreenEvent(tenant, eventKey) {
    return resolveRule(tenant, eventKey).mode === MODE_FULL_SCREEN;
}
